import defaultsDeep from 'lodash/defaultsDeep'
import reduce from 'lodash/reduce'
import random from 'lodash/random'
import actionTypes from 'constants/actionTypes'
import single from './single'

interface UserInput {
  id?: string
  username?: string
  index?: number
  [key: string]: unknown
}

type UsersAllState = Record<string, ReturnType<typeof single>>

interface PermissionUserInput {
  id: string
  username?: string
  index?: number
  [key: string]: unknown
}

interface UsersAllAction {
  type?: string
  payload?: {
    id?: string
    username?: string
    ids?: string[]
    data?: UserInput[]
    readers?: PermissionUserInput[]
    writers?: PermissionUserInput[]
  }
  previousPayload?: {
    ids?: string[]
  }
}

const toUserMap = (users: UserInput[], state: UsersAllState, action: UsersAllAction): UsersAllState => {
  return reduce(users, (acc: UsersAllState, user: UserInput) => {
    if (!user.id) return acc
    acc[user.id] = single(state[user.id], action, user)
    return acc
  }, {})
}

export default (state: UsersAllState = {}, action: UsersAllAction): UsersAllState => {
  switch (action.type) {
    case actionTypes.USERS.GET.REQUEST: {
      const users = reduce(action.payload?.ids ?? [], (acc: UsersAllState, id: string) => {
        const index = state[id]?.index || random(30)
        acc[id] = single(state[id], action, { id, index })
        return acc
      }, {})
      return defaultsDeep(users, state)
    }
    case actionTypes.USERS.GET.RECEIVE: {
      const users = toUserMap(action.payload?.data ?? [], state, action)
      return defaultsDeep(users, state)
    }
    case actionTypes.USERS.GET.FAIL: {
      const users = reduce(action.previousPayload?.ids ?? [], (acc: UsersAllState, id: string) => {
        acc[id] = single(state[id], action, { id })
        return acc
      }, {})
      return defaultsDeep(users, state)
    }
    case actionTypes.PERMISSIONS.NEW_CONTENT.READERS.ADD:
    case actionTypes.PERMISSIONS.NEW_CONTENT.WRITERS.ADD: {
      const id = action.payload?.id
      if (!id) return state
      const index = state[id]?.index || random(30)
      const user: UserInput = {
        id,
        index,
      }
      if (typeof action.payload?.username === 'string') {
        user.username = action.payload.username
      }
      return defaultsDeep(
        {
          [id]: single(state[id], action, user)
        },
        state
      )
    }
    case actionTypes.PERMISSIONS.NEW_CONTENT.SET: {
      const readers = reduce(action.payload?.readers ?? [], (rs: UsersAllState, r: PermissionUserInput) => {
        r.index = random(30)
        rs[r.id] = single(state[r.id], action, r)
        return rs
      }, {})
      const writers = reduce(action.payload?.writers ?? [], (ws: UsersAllState, w: PermissionUserInput) => {
        w.index = random(30)
        ws[w.id] = single(state[w.id], action, w)
        return ws
      }, {})
      return defaultsDeep(readers, writers, state)
    }
  }
  return state
}
